import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { Credentials, LoginResponse, MfaLoginPayload, UserResponse } from './types';
import { completeMfaLoginApi, loginApi, verifyTokenApi } from './authApi';
import { Logger } from '../../lib/utils';

export interface AuthState {
  token: string | null;
  user: UserResponse | null;
  isAuthenticated: boolean;
  initialized: boolean;
  loginLoading: boolean;
  loginError: string | null;
  verifyError: string | null;
  mfaPending: { mfaToken: string; user: UserResponse } | null;
}

const loadAuthState = (): Omit<AuthState, 'loginLoading' | 'loginError' | 'verifyError' | 'mfaPending'> => {
  try {
    const token = localStorage.getItem('token');
    const userString = localStorage.getItem('user');
    let user: UserResponse | null = null;

    if (userString && userString !== 'undefined' && userString !== '{}') {
      try {
        user = JSON.parse(userString);
        Logger.debug('AuthSlice: Usuario cargado desde localStorage', { role: user?.role });
      } catch (e) {
        Logger.error('Error al analizar el usuario desde localStorage:', e);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return { user: null, token: null, isAuthenticated: false, initialized: true };
      }
    }

    if (token && user) {
      return { user, token, isAuthenticated: true, initialized: true };
    }

    return { user: null, token: null, isAuthenticated: false, initialized: true };
  } catch (error) {
    Logger.error('Error al cargar el estado de autenticación:', error);
    return { user: null, token: null, isAuthenticated: false, initialized: true };
  }
};

export const loginUser = createAsyncThunk<LoginResponse, Credentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      return await loginApi(credentials);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string; code?: string } } };
      const status = axiosErr.response?.status;
      const code = axiosErr.response?.data?.code;
      const message = axiosErr.response?.data?.message;
      if (status === 401) return rejectWithValue('Credenciales inválidas');
      if (status === 403 && code === 'mfa_enrollment_required') {
        return rejectWithValue(message || 'Tu rol requiere MFA. Activá la autenticación de dos factores en Mi perfil.');
      }
      if (status === 403) return rejectWithValue('Usuario no autorizado');
      return rejectWithValue(message || 'Error al iniciar sesión');
    }
  },
);

export const completeMfaLogin = createAsyncThunk<LoginResponse, MfaLoginPayload>(
  'auth/completeMfaLogin',
  async (payload, { rejectWithValue }) => {
    try {
      return await completeMfaLoginApi(payload);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(axiosErr.response?.data?.message || 'Código de autenticación incorrecto');
    }
  },
);

export const verifyUserToken = createAsyncThunk<void, void>(
  'auth/verifyToken',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await verifyTokenApi();
    } catch {
      dispatch(logout());
      return rejectWithValue('Token inválido');
    }
  },
);

const persisted = loadAuthState();

const initialState: AuthState = {
  ...persisted,
  loginLoading: false,
  loginError: null,
  verifyError: null,
  mfaPending: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<LoginResponse>) => {
      const { token, data: user } = action.payload;
      if (!token || !user) return;
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      state.initialized = true;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    },
    logout: state => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.initialized = true;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    setCurrentUser: (state, action: PayloadAction<UserResponse>) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    updateProfile: (state, action: PayloadAction<Partial<UserResponse>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
    clearLoginError: (state) => {
      state.loginError = null;
    },
    clearMfaPending: (state) => {
      state.mfaPending = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loginLoading = true;
        state.loginError = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loginLoading = false;
        const { token, data: user, mfaRequired, mfaToken } = action.payload;
        if (mfaRequired && mfaToken && user) {
          state.mfaPending = { mfaToken, user };
          return;
        }
        if (!token || !user) return;
        state.mfaPending = null;
        state.token = token;
        state.user = user;
        state.isAuthenticated = true;
        state.initialized = true;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginLoading = false;
        state.loginError = (action.payload as string) || 'Error al iniciar sesión';
      })
      .addCase(completeMfaLogin.pending, (state) => {
        state.loginLoading = true;
        state.loginError = null;
      })
      .addCase(completeMfaLogin.fulfilled, (state, action) => {
        state.loginLoading = false;
        const { token, data: user } = action.payload;
        if (!token || !user) return;
        state.mfaPending = null;
        state.token = token;
        state.user = user;
        state.isAuthenticated = true;
        state.initialized = true;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
      })
      .addCase(completeMfaLogin.rejected, (state, action) => {
        state.loginLoading = false;
        state.loginError = (action.payload as string) || 'Código de autenticación incorrecto';
      })
      .addCase(verifyUserToken.rejected, (state, action) => {
        state.verifyError = (action.payload as string) || 'Token inválido';
      });
  },
});

export const { setCredentials, logout, setCurrentUser, updateProfile, clearLoginError, clearMfaPending } = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectCurrentToken = (state: RootState) => state.auth.token;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectLoginLoading = (state: RootState) => state.auth.loginLoading;
export const selectLoginError = (state: RootState) => state.auth.loginError;
export const selectMfaPending = (state: RootState) => state.auth.mfaPending;
